import {
  CompanyDetailsDto,
  CompanyDto,
  ContactPersonDto,
  CreateCompanyDto,
  IndustryDto,
  UpdateCompanyDto,
  CompanyContactPersonNoteDto,
} from "../types/companyTypes";
import api from "../utils/api";
import { AxiosResponse } from "axios";

/**
 * API call to retrieve a list of all companies
 * @returns Array of company DTOs
 */
export const getAllCompanies = async (): Promise<AxiosResponse<CompanyDto[]>> => {
  return await api.get<CompanyDto[]>(`/companies`);
};

/**
 * API call to retrieve a list of all industries
 * @returns Array of industry DTOs
 */
export const getAllIndustries = async (): Promise<AxiosResponse<IndustryDto[]>> => {
  return await api.get<IndustryDto[]>(`/companies/industries`);
};

/**
 * API call to retrieve detailed information about a specific company
 * @param companyId The ID of the company to retrieve details for
 * @returns Company details DTO
 */
export const getCompanyDetails = async (companyId: number): Promise<AxiosResponse<CompanyDetailsDto>> => {
  return await api.get<CompanyDetailsDto>(`/companies/${companyId}`);
};

/**
 * API call to retrieve all contact persons for a specific company
 * @param companyId The ID of the company to get contact persons for
 * @returns Array of contact person DTOs
 */
export const getCompanyContactPersons = async (companyId: number): Promise<AxiosResponse<ContactPersonDto[]>> => {
  return await api.get<ContactPersonDto[]>(`/companies/${companyId}/contact-persons`);
};

/**
 * API call to create a new company
 * @param companyData The data for the company to create
 * @returns The created company as CompanyDetailsDto
 */
export const createCompany = async (companyData: CreateCompanyDto): Promise<AxiosResponse<CompanyDetailsDto>> => {
  return await api.post<CompanyDetailsDto>(`/companies`, companyData);
};

/**
 * API call to update an existing company
 * @param companyId The ID of the company to update
 * @param companyData The data to update the company with
 * @returns The updated company as CompanyDetailsDto
 */
export const updateCompany = async (
  companyId: number,
  companyData: UpdateCompanyDto
): Promise<AxiosResponse<CompanyDetailsDto>> => {
  return await api.put<CompanyDetailsDto>(`/companies/${companyId}`, companyData);
};

/**
 * API call to retrieve all notes of all contact persons for a specific company
 * @param companyId The ID of the company to get contact persons for
 * @returns Array of contact person DTOs
 */
export const getCompanyContactPersonNotes = async (
  companyId: number
): Promise<AxiosResponse<CompanyContactPersonNoteDto[]>> => {
  return await api.get<CompanyContactPersonNoteDto[]>(`/companies/${companyId}/contact-persons/notes`);
};
